import { normalizeUsername, validateUsername } from "./usernameValidation";
import { validatePasswordValues } from "./passwordValidation";

const trim = (value) => value.trim();
const normalizeEmail = (value) => trim(value).toLowerCase();
const normalizeCellphoneDigits = (value) => trim(value).replace(/\D/g, "");
const birthDatePattern = /^\d{4}-\d{2}-\d{2}$/;
const cellphoneDigitsPattern = /^(?:\+?598\d{8}|\d{8})$/;
const surnamePattern = /^[\p{L}' -]+$/u;
const minorAgeLimit = 13;
const schoolBirthYearLimit = 2007;
const tnuaBirthYearLimit = 2004;

function parseDateOnly(value) {
  const [year, month, day] = value.split("-").map((part) => Number.parseInt(part, 10));
  return new Date(Date.UTC(year, month - 1, day));
}

function isValidBirthDateString(value) {
  if (!birthDatePattern.test(value)) {
    return false;
  }

  const birthDate = parseDateOnly(value);
  return !Number.isNaN(birthDate.getTime());
}

function calculateAge(birthDateValue) {
  const birthDate = parseDateOnly(birthDateValue);
  const today = new Date();

  let age = today.getUTCFullYear() - birthDate.getUTCFullYear();
  const monthDifference = today.getUTCMonth() - birthDate.getUTCMonth();

  if (
    monthDifference < 0 ||
    (monthDifference === 0 && today.getUTCDate() < birthDate.getUTCDate())
  ) {
    age -= 1;
  }

  return age;
}

export function isUnderThirteen(birthDateValue) {
  if (!isValidBirthDateString(birthDateValue)) {
    return false;
  }

  return calculateAge(birthDateValue) < minorAgeLimit;
}

export function isBornAfterYear(birthDateValue, year) {
  if (!isValidBirthDateString(birthDateValue)) {
    return false;
  }

  return parseDateOnly(birthDateValue).getUTCFullYear() > year;
}

function isValidCellphone(value) {
  const normalized = value.replace(/[^\d+]/g, "");
  return cellphoneDigitsPattern.test(normalized);
}

function normalizeUruguayCellphone(value) {
  const digits = normalizeCellphoneDigits(value);

  if (!digits) {
    return "";
  }

  const localDigits = digits.startsWith("598") ? digits.slice(3) : digits;
  const strippedLeadingZeros = localDigits.replace(/^0+/, "");

  if (!strippedLeadingZeros) {
    return "";
  }

  return `+598${strippedLeadingZeros.slice(0, 8)}`;
}

export function normalizeRegisterValues(values) {
  return {
    name: trim(values.name),
    lastName: trim(values.lastName),
    cellphone: normalizeUruguayCellphone(values.cellphone),
    birthDate: trim(values.birthDate),
    cityOfOrigin: trim(values.cityOfOrigin),
    username: normalizeUsername(values.username),
    email: normalizeEmail(values.email),
    password: values.password,
    passwordConfirm: values.passwordConfirm,
    institutionSelection: trim(values.institutionSelection),
    institutionName: trim(values.institutionName),
    tnuaId: trim(values.tnuaId),
    parentName: trim(values.parentName),
    parentLastName: trim(values.parentLastName),
    parentCellphone: normalizeUruguayCellphone(values.parentCellphone),
    parentEmail: normalizeEmail(values.parentEmail),
  };
}

function isValidSurname(value) {
  const normalized = trim(value);

  if (!normalized || !surnamePattern.test(normalized)) {
    return false;
  }

  const letterCount = (normalized.match(/\p{L}/gu) ?? []).length;
  return letterCount > 3;
}

export function validateRegisterValues(values) {
  const errors = {};
  const normalized = normalizeRegisterValues(values);
  const requiresParentInfo = isUnderThirteen(normalized.birthDate);
  const requiresSchoolInfo = isBornAfterYear(normalized.birthDate, schoolBirthYearLimit);
  const requiresTnuaInfo = isBornAfterYear(normalized.birthDate, tnuaBirthYearLimit);

  if (!normalized.name) errors.name = "El nombre es obligatorio.";
  if (!normalized.lastName) {
    errors.lastName = "El apellido es obligatorio.";
  } else if (!isValidSurname(normalized.lastName)) {
    errors.lastName = "El apellido debe tener más de 3 letras y usar solo letras, espacios o guiones.";
  }

  const usernameError = validateUsername(normalized.username);
  if (usernameError) {
    errors.username = usernameError;
  }

  if (!requiresParentInfo) {
    if (!normalized.cellphone) {
      errors.cellphone = "El número de celular es obligatorio.";
    } else if (!isValidCellphone(normalized.cellphone)) {
      errors.cellphone = "Ingresá un número de celular uruguayo válido.";
    }

    if (!normalized.email) {
      errors.email = "El email es obligatorio.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized.email)) {
      errors.email = "Ingresá un email válido.";
    }
  }

  if (requiresSchoolInfo) {
    if (!normalized.institutionSelection) {
      errors.institutionSelection = "Elegí una escuela/liceo.";
    } else if (
      normalized.institutionSelection === "__new__" &&
      !normalized.institutionName
    ) {
      errors.institutionName = "Ingresá el nombre de la nueva institución.";
    } else if (normalized.institutionName && normalized.institutionName.length > 120) {
      errors.institutionName = "El nombre de la institución es demasiado largo.";
    }
  } else if (normalized.institutionName) {
    errors.institutionName = "La institución no corresponde para esta fecha de nacimiento.";
  }

  if (requiresTnuaInfo && !normalized.tnuaId) {
    errors.tnuaId = "Elegí una tnuá.";
  }

  Object.assign(errors, validatePasswordValues(normalized, { requireSpecialCharacter: false }));

  if (isUnderThirteen(normalized.birthDate)) {
    if (!normalized.parentName) {
      errors.parentName = "El nombre del padre, madre o tutor es obligatorio.";
    }

    if (!normalized.parentLastName) {
      errors.parentLastName = "El apellido del padre, madre o tutor es obligatorio.";
    }

    if (!normalized.parentCellphone) {
      errors.parentCellphone = "El celular del padre, madre o tutor es obligatorio.";
    } else if (!isValidCellphone(normalized.parentCellphone)) {
      errors.parentCellphone = "Ingresá un número de celular uruguayo válido.";
    }

    if (!normalized.parentEmail) {
      errors.parentEmail = "El correo del padre, madre o tutor es obligatorio.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized.parentEmail)) {
      errors.parentEmail = "Ingresá un email válido.";
    }
  }

  return errors;
}
